import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useNotificationReadStatus } from "@/hooks/useNotificationReadStatus";
import { SupabaseNotification } from "@/types/notifications";
import { savePreferences } from "@/utils/notificationPreferences";

interface UseNotificationFiltersProps {
  sortedNotifications: SupabaseNotification[];
  unreadCount?: number;
}

export const useNotificationFilters = ({
  sortedNotifications,
  unreadCount = 0
}: UseNotificationFiltersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isNotificationRead } = useNotificationReadStatus();
  
  // Read selectedStatuses directly from URL - single source of truth
  const selectedStatuses = useMemo(() => {
    const statusParam = searchParams.get('status');
    if (!statusParam || statusParam === 'all') {
      return [];
    }
    return statusParam.split(',').filter(Boolean);
  }, [searchParams]);
  
  // Read unreadFilter directly from URL - single source of truth
  const unreadFilter = useMemo(() => 
    searchParams.get('unread') === 'true', 
    [searchParams]
  );
  
  // Helper to check if a notification matches any selected status (including aliases)
  const matchesStatus = (notification: SupabaseNotification, filterStatus: string): boolean => {
    const notificationStatus = notification.status;
    
    if (filterStatus === "open") {
      return notificationStatus === "open" || notificationStatus === "actie_luijten";
    }
    if (filterStatus === "in_behandeling") {
      return notificationStatus === "in_behandeling" || notificationStatus === "actie_decoprof";
    }
    if (filterStatus === "opgelost") {
      return notificationStatus === "opgelost" || notificationStatus === "afgerond";
    }
    
    // Show all afwachting_voorraad notifications regardless of stock status
    // This allows the StockAlertBadge to be displayed when stock is available
    if (filterStatus === "afwachting_voorraad") {
      return notificationStatus === "afwachting_voorraad";
    }
    
    return notificationStatus === filterStatus;
  };
  
  const statusFilteredNotifications = useMemo(() => {
    if (selectedStatuses.length === 0) {
      return sortedNotifications;
    }
    
    return sortedNotifications.filter(notification => 
      selectedStatuses.some(filterStatus => matchesStatus(notification, filterStatus))
    );
  }, [selectedStatuses, sortedNotifications]);
  
  // Calculate filtered unread count
  const filteredUnreadCount = useMemo(() => {
    return statusFilteredNotifications.filter(
      notification => !isNotificationRead(notification.id)
    ).length;
  }, [statusFilteredNotifications, isNotificationRead]);
  
  const displayedNotifications = useMemo(() => {
    return unreadFilter 
      ? statusFilteredNotifications.filter(notification => !isNotificationRead(notification.id))
      : statusFilteredNotifications;
  }, [unreadFilter, statusFilteredNotifications, isNotificationRead]);

  const handleStatusChange = (statuses: string[]) => {
    const newParams = new URLSearchParams(window.location.search);
    
    if (statuses.length === 0) {
      newParams.delete('status');
    } else {
      newParams.set('status', statuses.join(','));
    }
    
    setSearchParams(newParams, { replace: true });
    savePreferences({ selectedStatuses: statuses });
  };

  const toggleUnreadFilter = () => {
    const newParams = new URLSearchParams(window.location.search);
    const newValue = !unreadFilter;
    
    if (newValue) {
      newParams.set('unread', 'true');
    } else {
      newParams.delete('unread');
    }
    
    setSearchParams(newParams, { replace: true });
    savePreferences({ unreadFilter: newValue });
  };

  return {
    unreadFilter,
    selectedStatuses,
    filteredUnreadCount,
    statusFilteredNotifications,
    displayedNotifications,
    handleStatusChange,
    toggleUnreadFilter
  };
};